import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Section } from './useHcoFieldNames';

export const HcpSections = {
	...Section,
	Professional: 'professional',
	Demographics: 'demographics'
} as const;

export type HcpSection = (typeof HcpSections)[keyof typeof HcpSections];

export const useHcpFieldNames = (countryId?: string | null) => {
	const { t } = useTranslation();

	const hcpSections: Record<HcpSection, string> = useMemo(
		() => ({
			[HcpSections.Name]: t('Name'),
			[HcpSections.Professional]: t('Professional'),
			[HcpSections.Ids]: t('IDs'),
			[HcpSections.Demographics]: t('Demographics'),
			[HcpSections.Contact]: t('Contact'),
			[HcpSections.Affiliations]: t('Affiliation'),
			[HcpSections.Addresses]: t('Address'),
			[HcpSections.Licenses]: t('License')
		}),
		[t]
	);

	const hcpSectionMap = useMemo(
		() => ({
			// Name
			firstNameCda_V: HcpSections.Name,
			middleNameCda_V: HcpSections.Name,
			lastNameCda_V: HcpSections.Name,
			prefixCda_V: HcpSections.Name,
			suffixCda_V: HcpSections.Name,
			internationalNameCda_V: HcpSections.Name,

			// Professional
			hcpTypeCda_V: HcpSections.Professional,
			hcpTypeLocal_V: HcpSections.Professional,
			degree1Cda_V: HcpSections.Professional,
			allSpecLocal_V: HcpSections.Professional,
			spec1Local_V: HcpSections.Professional,
			spec2Local_V: HcpSections.Professional,
			spec1Cda_V: HcpSections.Professional,
			specialty1_V: HcpSections.Professional,
			specialty2_V: HcpSections.Professional,
			specialty3_V: HcpSections.Professional,
			specialty4_V: HcpSections.Professional,
			specialty5_V: HcpSections.Professional,
			specialty6_V: HcpSections.Professional,
			specialty7_V: HcpSections.Professional,
			specialty8_V: HcpSections.Professional,
			specialty9_V: HcpSections.Professional,
			specialty10_V: HcpSections.Professional,
			primaryCountry_V: HcpSections.Professional,

			prescriberCda_V: HcpSections.Professional,
			telehealth_V: HcpSections.Professional,
			inCurrentPecos_V: HcpSections.Professional,
			nhidCda_V: HcpSections.Ids,
			nhid2_V: HcpSections.Ids,
			hcpTaxId_V: HcpSections.Ids,
			statusCda_V: HcpSections.Professional,
			hcpStatus_V: HcpSections.Professional,
			hcpReasonStatus_V: HcpSections.Professional,

			// Demographics
			languageCda_V: HcpSections.Demographics,
			gender_V: HcpSections.Demographics,
			birthYear_V: HcpSections.Demographics,
			gradSchool_V: HcpSections.Demographics,
			gradYear_V: HcpSections.Demographics,
			// dataPrivacyOptOut: HcpSections.Demographics,

			// Contact
			officePhoneCda_V: HcpSections.Contact,
			phoneCda_V: HcpSections.Contact,
			faxCda_V: HcpSections.Contact,
			emailCda_V: HcpSections.Contact,
			email2_V: HcpSections.Contact,
			email3_V: HcpSections.Contact,
			email4_V: HcpSections.Contact,
			email5_V: HcpSections.Contact,
			websiteCda_V: HcpSections.Contact,
			website_V: HcpSections.Contact,

			// Affiliations // Note (Ben): Shouldn't be needed as not 'root' group
			parentHcos_V: HcpSections.Affiliations,
			parentHcoVid_V: HcpSections.Affiliations,
			parentHcoStatus_V: HcpSections.Affiliations,
			isPrimaryRelationship_V: HcpSections.Affiliations,
			hcoNameCda_V: HcpSections.Affiliations,
			hcoTypeCda_V: HcpSections.Affiliations,
			locationRole_V: HcpSections.Affiliations,
			jobTitle_V: HcpSections.Affiliations,
			role_V: HcpSections.Affiliations,
			typeOfRelationship_V: HcpSections.Affiliations,
			relationshipType_V: HcpSections.Affiliations,
			principalAffiliationName_V: HcpSections.Affiliations,

			// Addresses // Note (Ben): Shouldn't be needed as not 'root' group
			addresses_V: HcpSections.Addresses,
			primaryCda_V: HcpSections.Addresses,
			streetAddress1Cda_V: HcpSections.Addresses,
			streetAddress2Cda_V: HcpSections.Addresses,
			cityCda_V: HcpSections.Addresses,
			stateCda_V: HcpSections.Addresses,
			postalCodeCda_V: HcpSections.Addresses,
			postalCodeFullUs_V: HcpSections.Addresses,
			countryCda_V: HcpSections.Addresses,
			coordinates_V: HcpSections.Addresses,
			subStateAdminUnit_V: HcpSections.Addresses,
			subAdministrativeArea_V: HcpSections.Addresses,
			cbsa_V: HcpSections.Addresses,
			phone1_V: HcpSections.Addresses,
			phone2_V: HcpSections.Addresses,
			fax1_V: HcpSections.Addresses,

			// Licenses // Note (Ben): Shouldn't be needed as not 'root' group
			licenseNumber_V: HcpSections.Licenses,
			licenseDegree_V: HcpSections.Licenses,
			typeValue_V: HcpSections.Licenses,
			expirationDate_V: HcpSections.Licenses,
			gracePeriod_V: HcpSections.Licenses,
			rxaEligible_V: HcpSections.Licenses,
			type_V: HcpSections.Licenses,
			licenseSubtype_V: HcpSections.Licenses,
			body_V: HcpSections.Licenses,
			licenseCertification_V: HcpSections.Licenses,
			bestStateLicense_V: HcpSections.Licenses,
			licenseStatus_V: HcpSections.Licenses,
			licenseEligibility_V: HcpSections.Licenses,
			licenseStatusCondition_V: HcpSections.Licenses,
			comprehensivePrescriptiveAuthority_V: HcpSections.Licenses,
			comprehensiveSampleEligibility_V: HcpSections.Licenses,
			collabAgreeRequired_V: HcpSections.Licenses,
			ctrlSubstancePrescriptive_V: HcpSections.Licenses,
			ctrlSubstanceSample_V: HcpSections.Licenses,
			deaBusinessActivityCode_V: HcpSections.Licenses,
			deaPaymentIndicator_V: HcpSections.Licenses,
			drugSchedule_V: HcpSections.Licenses,

			// Other Attributes
			jobType_V: HcpSections.Professional,
			certifiedTrainer_V: HcpSections.Professional,
			summary_V: HcpSections.Professional
		}),
		[]
	);

	const hcpFieldNames = useMemo(
		() => ({
			// Professional
			firstNameCda_V: t('First Name'),
			middleNameCda_V: t('Middle Name'),
			lastNameCda_V: t('Last Name'),
			internationalNameCda_V: t('International Name'),
			hcpTypeCda_V: t('Global HCP Type'),
			hcpTypeLocal_V: t('Local HCP Type'),
			degree1Cda_V: t('Medical Degree'),

			primaryCountry_V: t('Country'),

			allSpecLocal_V: t('Local Specialties'),
			spec1Local_V: t('Primary Local Specialty'),
			spec2Local_V: t('Secondary Local Specialty'),
			spec1Cda_V: t('Global Specialty'),
			specialty1_V: t('Primary Local Specialty'),
			specialty2_V: t('Local Specialty 2'),
			specialty3_V: t('Local Specialty 3'),
			specialty4_V: t('Local Specialty 4'),
			specialty5_V: t('Local Specialty 5'),
			specialty6_V: t('Local Specialty 6'),
			specialty7_V: t('Local Specialty 7'),
			specialty8_V: t('Local Specialty 8'),
			specialty9_V: t('Local Specialty 9'),
			specialty10_V: t('Local Specialty 10'),

			statusCda_V: t('Status'),
			hcpStatus_V: t('Status Reason'),
			hcpReasonStatus_V: t('Status Reason'),
			nhidCda_V: t('National Healthcare ID'),
			nhid2_V: t('Secondary Healthcare ID'),
			prefixCda_V: t('Prefix'),
			suffixCda_V: t('Suffix'),

			// Demographics
			languageCda_V: t('Language'),
			gender_V: t('Gender'),
			birthYear_V: t('Year of Birth'),
			// dataPrivacyOptOut: t('OpenData Data Privacy Opt Out'),

			// Contact
			officePhoneCda_V: t('Office Phone'),
			phoneCda_V: t('Phone'),
			faxCda_V: t('Fax'),
			// Only US HCPs support multiple emails, so only they get the numbered
			// "Email 1" form - non-US HCPs always just get "Email" (matches
			// ContactSection.tsx / useDcrFields.ts's identical logic).
			emailCda_V:
				countryId === 'us' ? t('Email {{number}}', { number: 1 }) : t('Email'),
			email2_V: t('Email {{number}}', { number: 2 }),
			email3_V: t('Email {{number}}', { number: 3 }),
			email4_V: t('Email {{number}}', { number: 4 }),
			email5_V: t('Email {{number}}', { number: 5 }),
			websiteCda_V: t('Website'),

			// Affiliations
			parentHcos_V: t('Affiliation'),
			parentHcoVid_V: t('ID'),
			parentHcoStatus_V: t('Status'),
			isPrimaryRelationship_V: t('Is Primary'),
			hcoNameCda_V: t('Name'),
			hcoTypeCda_V: t('Global HCO Type'),
			locationRole_V: t('Location'),
			jobTitle_V: t('Job Title'),
			role_V: t('Job Title'),
			typeOfRelationship_V: t('Type of Relationship'),
			relationshipType_V: t('Type of Relationship'),
			principalAffiliationName_V: t('Principle Name'),

			// Addresses
			addresses_V: t('Address'),
			primaryCda_V: t('Is Primary'),
			streetAddress1Cda_V: t('Street Address 1'),
			streetAddress2Cda_V: t('Street Address 2'),
			cityCda_V: t('City'),
			stateCda_V: t('State'),
			postalCodeCda_V: t('Postal Code'),
			postalCodeFullUs_V: t('Full Postal Code US'),
			countryCda_V: t('Country'),
			coordinates_V: t('Coordinates'),
			subStateAdminUnit_V: t('Region/County'),
			subAdministrativeArea_V: t('Region/County'),
			cbsa_V: t('Metropolitan (CBSA)'),
			phone1_V: t('Phone 1'),
			phone2_V: t('Phone 2'),
			fax1_V: t('Fax'),

			// Licenses
			licenseNumber_V: t('License ID'),
			licenseDegree_V: t('License Degree'),
			typeValue_V: t('Licensing Authority'),
			expirationDate_V: t('Expiration Date'),
			gracePeriod_V: t('Grace Expiry'),
			rxaEligible_V: t('Sample Eligibility'),
			type_V: t('License Type'),
			licenseSubtype_V: t('License SubType'),
			body_V: t('Licensing Body'),
			licenseCertification_V: t('License Certification'),
			bestStateLicense_V: t('Best State License'),
			licenseStatus_V: t('License Status'),
			licenseEligibility_V: t('Prescriptive Authority'),
			licenseStatusCondition_V: t('License Status Condition'),
			comprehensivePrescriptiveAuthority_V: t(
				'Comprehensive Prescriptive Authority'
			),
			comprehensiveSampleEligibility_V: t('Comprehensive Sample Eligibility'),
			collabAgreeRequired_V: t('Collaborative Agreement Required'),
			ctrlSubstancePrescriptive_V: t(
				'Controlled Substances Prescriptive Authority'
			),
			ctrlSubstanceSample_V: t('Controlled Substances Sample Eligibility'),
			deaBusinessActivityCode_V: t('DEA Activity Code'),
			deaPaymentIndicator_V: t('DEA Payment Indicator'),
			drugSchedule_V: t('Drug Schedule Details'),

			// Local Attributes
			jobType_V: t('Job Type'),
			certifiedTrainer_V: t('Certified Trainings'),
			gradSchool_V: t('Graduation School'),
			gradYear_V: t('Graduation Year'),
			hcpTaxId_V: t('Tax ID'),
			summary_V: t('Summary'),
			prescriberCda_V: t('Prescriber'),
			website_V: t('Website'),
			telehealth_V: t('Telehealth'),
			inCurrentPecos_V: t('In Current PECOS')
		}),
		[t, countryId]
	);

	return {
		hcpFieldNames,
		hcpSectionMap,
		hcpSections
	};
};
